(() => {
  const parsedCache = new WeakMap();
  const inflightByCard = new WeakMap();
  const THB_LOADING_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"><g><path style="transform:scale(0.8);transform-origin:50px 50px" stroke-linecap="round" d="M24.3 30C11.4 30 5 43.3 5 50s6.4 20 19.3 20c19.3 0 32.1-40 51.4-40C88.6 30 95 43.3 95 50s-6.4 20-19.3 20C56.4 70 43.6 30 24.3 30z" stroke-dasharray="42.76482137044271 42.76482137044271" stroke-width="8" stroke="#a87d7a" fill="none"><animate values="0;256.58892822265625" keyTimes="0;1" dur="1s" repeatCount="indefinite" attributeName="stroke-dashoffset"></animate></path><g></g></g></svg>';
  const PRELOAD_TIMEOUT_MS = 8000;
  const MAX_LOADING_MS = 12000;

  const getMaterialSimplified = (root) => {
    if (!root) return null;
    if (parsedCache.has(root)) return parsedCache.get(root);
    const raw = root.dataset?.materialSimplified;
    if (!raw) {
      parsedCache.set(root, null);
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      parsedCache.set(root, parsed);
      return parsed;
    } catch (err) {
      parsedCache.set(root, null);
      return null;
    }
  };

  const setActive = (root, el) => {
    if (!root || !el) return;
    const options = root.querySelectorAll('.material-variant-selector__option');
    options.forEach((opt) => {
      const isActive = opt === el;
      opt.classList.toggle('is-active', isActive);
      opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  };

  const preloadImages = (urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return Promise.resolve([]);
    const uniqueUrls = Array.from(new Set(urls.filter(Boolean)));
    const map = new Map();
    const promises = uniqueUrls.map(
      (url) =>
        new Promise((resolve) => {
          const img = new Image();
          let done = false;
          const finish = (ok) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            resolve({ url, ok });
          };
          const timer = setTimeout(() => finish(false), PRELOAD_TIMEOUT_MS);
          img.onload = () => finish(true);
          img.onerror = () => finish(false);
          img.src = url;
        })
    );
    return Promise.all(promises).then((results) => {
      results.forEach((r) => map.set(r.url, r.ok));
      return urls.map((url) => ({ url, ok: map.get(url) !== false }));
    });
  };

  const setImageUrl = (img, url) => {
    if (!img || !url) return;
    img.setAttribute('src', url);
    img.setAttribute('srcset', url);
    img.setAttribute('data-src', url);
    img.setAttribute('data-srcset', url);
  };

  const ensureLoadingOverlay = (container) => {
    if (!container) return null;
    let overlay = container.querySelector(':scope > .loading-overlay');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.innerHTML = THB_LOADING_SVG;
    container.appendChild(overlay);
    return overlay;
  };

  const updateCardImages = (productCard, galleryUrls) => {
    if (!productCard || !Array.isArray(galleryUrls) || galleryUrls.length === 0) return Promise.resolve(false);

    const link = productCard.querySelector('.product-featured-image-link');
    if (!link) return Promise.resolve(false);

    const requestId = (inflightByCard.get(productCard) || 0) + 1;
    inflightByCard.set(productCard, requestId);

    const primary = productCard.querySelector('img.product-primary-image');
    const secondaryImages = Array.from(productCard.querySelectorAll('img.product-secondary-image'));
    const primaryUrl = galleryUrls[0];
    const secondaryUrl = galleryUrls[1] || galleryUrls[0];
    const urlsToPreload = [primaryUrl, ...(secondaryImages.length ? [secondaryUrl] : [])].filter(Boolean);

    ensureLoadingOverlay(link);
    link.classList.add('loading');

    const maxTimer = setTimeout(() => {
      if (inflightByCard.get(productCard) === requestId) link.classList.remove('loading');
    }, MAX_LOADING_MS);

    return preloadImages(urlsToPreload).then((loaded) => {
      if (inflightByCard.get(productCard) !== requestId) return false;

      clearTimeout(maxTimer);
      const primaryLoadedUrl = loaded[0]?.ok ? loaded[0]?.url : null;
      if (primaryLoadedUrl) setImageUrl(primary, primaryLoadedUrl);
      if (secondaryImages.length > 0) {
        const secondaryLoadedUrl = loaded[1]?.ok ? loaded[1]?.url : null;
        const url = secondaryLoadedUrl || primaryLoadedUrl || null;
        if (url) secondaryImages.forEach((img) => setImageUrl(img, url));
      }
      link.classList.remove('loading');
      return true;
    }).catch(() => {
      clearTimeout(maxTimer);
      if (inflightByCard.get(productCard) === requestId) link.classList.remove('loading');
      return false;
    });
  };

  const updateFigure = (root, opt) => {
    const productCard = root?.closest?.('product-card') || root?.closest?.('.product-card');
    const parsed = getMaterialSimplified(root);
    if (!Array.isArray(parsed)) return;
    if (!productCard) return;

    const key = opt?.dataset?.material;
    let item = (key && parsed.find((x) => x && x.material_type === key)) || null;

    if (!item) {
      const options = Array.from(root.querySelectorAll('.material-variant-selector__option'));
      const idx = options.indexOf(opt);
      item = idx >= 0 ? parsed[idx] || null : null;
    }

    if (!item) return;
    const priceElement = productCard.querySelector('.price');
    if (priceElement && item.price) {
      const discountPercentage = Number(item.discount_percentage) || 0;
      const hasSale = Boolean(item.compare_at_price && discountPercentage > 0);
      priceElement.classList.toggle('price--on-sale', hasSale);

      if (hasSale) {
        priceElement.innerHTML = `
          <del><span class="amount">${item.compare_at_price}</span></del>
          <ins><span class="amount discounted">${item.price}</span></ins>
          <span class="price-discount-percentage" aria-label="${discountPercentage} percent off">(-${discountPercentage}%)</span>
        `;
      } else {
        priceElement.innerHTML = `<ins><span class="amount">${item.price}</span></ins>`;
      }
    }
    updateCardImages(productCard, item.gallery);
  };

  document.addEventListener('click', (e) => {
    const opt = e.target.closest('.material-variant-selector__option');
    if (!opt) return;
    const root = opt.closest('.material-variant-selector');
    if (!root) return;
    updateFigure(root, opt);
    setActive(root, opt);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const opt = e.target.closest('.material-variant-selector__option');
    if (!opt) return;
    const root = e.target.closest('.material-variant-selector');
    if (!root) return;
    updateFigure(root, opt);
    e.preventDefault();
    setActive(root, opt);
  });
})();
